/**
 * StepCard: reusable card for wizard steps (enabled/disabled, click, styling).
 * Single Responsibility: render card shell, delegate content to StepContent.
 * Open/Closed: styling via constants; behavior via props.
 */

import React from 'react';
import PropTypes from 'prop-types';
import StepContent from './StepContent';

const CARD_BASE_CLASS =
  'bg-white content-stretch flex min-h-[320px] sm:min-h-[360px] lg:min-h-[400px] items-center px-4 pt-4 pb-6 sm:px-5 sm:pt-5 sm:pb-7 lg:px-6 lg:pt-6 lg:pb-10 rounded-xl w-full min-w-0 relative overflow-hidden';
const CARD_ENABLED_CLASS =
  'cursor-pointer transition-all duration-200 hover:shadow-[0_0_0_1px_rgba(97,184,231,0.4),0_12px_28px_-8px_rgba(97,184,231,0.25)]';
const CARD_DISABLED_CLASS = 'cursor-not-allowed';
const BORDER_BASE_CLASS =
  'absolute inset-0 pointer-events-none rounded-xl';
const BORDER_ENABLED_CLASS =
  'border-2 border-[#61b8e7]/90 shadow-[0_0_0_1px_rgba(97,184,231,0.3),0_4px_24px_-4px_rgba(97,184,231,0.15),0px_2px_12px_0px_rgba(0,0,0,0.08)]';
const BORDER_DISABLED_CLASS =
  'border-2 border-[#d9d9d9] shadow-[0px_2px_12px_0px_rgba(0,0,0,0.08)]';

function getCardClassName(isEnabled) {
  return `${CARD_BASE_CLASS} ${isEnabled ? CARD_ENABLED_CLASS : CARD_DISABLED_CLASS}`;
}

function getBorderClassName(isEnabled) {
  return `${BORDER_BASE_CLASS} ${isEnabled ? BORDER_ENABLED_CLASS : BORDER_DISABLED_CLASS}`;
}

const StepCard = ({
  isEnabled = false,
  stepNumber,
  title,
  copy,
  buttonLabel,
  iconComponent,
  onClick,
  stats = {},
  getDynamicLabel,
}) => {
  const handleClick = () => {
    if (isEnabled && onClick) {
      onClick(stepNumber);
    }
  };

  const handleKeyDown = (e) => {
    if (isEnabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className={getCardClassName(isEnabled)}
      onClick={handleClick}
      role={isEnabled ? 'button' : undefined}
      tabIndex={isEnabled ? 0 : -1}
      onKeyDown={handleKeyDown}
      aria-label={`${title} - ${isEnabled ? 'Click to proceed' : 'Step disabled'}`}
      data-name={`Step ${stepNumber} card`}
    >
      <div aria-hidden="true" className={getBorderClassName(isEnabled)} />
      <StepContent
        stepNumber={stepNumber}
        title={title}
        copy={copy}
        isEnabled={isEnabled}
        buttonLabel={buttonLabel}
        iconComponent={iconComponent}
        stats={stats}
        getDynamicLabel={getDynamicLabel}
      />
    </div>
  );
};

StepCard.propTypes = {
  isEnabled: PropTypes.bool,
  stepNumber: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  copy: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  buttonLabel: PropTypes.string,
  iconComponent: PropTypes.node,
  onClick: PropTypes.func,
  stats: PropTypes.object,
  getDynamicLabel: PropTypes.func,
};

export default StepCard;
